"use client";

import {createAlert} from "@gluestack-ui/alert";
import {PrimitiveIcon} from "@gluestack-ui/icon";
import type {VariantProps} from "@gluestack-ui/nativewind-utils";
import {useStyleContext, withStyleContext} from "@gluestack-ui/nativewind-utils/withStyleContext";
import {cssInterop} from "nativewind";
import React, {useMemo} from "react";

import {AlertCircleIcon, CheckedSolidCircleIcon, CloseIcon, InfoIcon} from "../../../react";
import {Box} from "../box";
import {Button, ButtonIcon} from "../button";
import {Center} from "../center";
import {HStack} from "../hstack";
import {Link, LinkText} from "../link";
import {Text} from "../text";
import {VStack} from "../vstack";

import {
    alertContainerStyle,
    alertIconStyle,
    alertStyle,
    alertTextStyle,
    iconWrapperInsideStyle,
    iconWrapperOutsideStyle
} from "./styles";

const SCOPE = "ALERT";

export const UIAlert = createAlert({
    Root: withStyleContext(Box, SCOPE),
    Text,
    Icon: PrimitiveIcon
});

cssInterop(PrimitiveIcon, {
    className: {
        target: "style",
        nativeStyleToProp: {
            height: true,
            width: true,
            // @ts-ignore
            fill: true,
            color: "classNameColor",
            stroke: true
        }
    }
});

type IAlertProps = Omit<React.ComponentPropsWithoutRef<typeof UIAlert>, "context"> &
    VariantProps<typeof alertStyle>;

const AlertUI = React.forwardRef<React.ElementRef<typeof UIAlert>, IAlertProps>(
    ({className, variant = "solid", action = "muted", ...props}, ref) => {
        return (
            <UIAlert
                ref={ref}
                className={alertStyle({action, variant, className})}
                context={{variant, action}}
                {...props}
            />
        );
    }
);

type IAlertTextProps = React.ComponentPropsWithoutRef<typeof UIAlert.Text> &
    VariantProps<typeof alertTextStyle>;

const AlertText = React.forwardRef<React.ElementRef<typeof UIAlert.Text>, IAlertTextProps>(
    ({className, ...props}, ref) => {
        const {action: parentAction} = useStyleContext(SCOPE);
        return (
            <UIAlert.Text
                className={alertTextStyle({
                    class: className,
                    parentVariants: {
                        action: parentAction
                    }
                })}
                {...props}
                ref={ref}
            />
        );
    }
);

type IAlertIconProps = React.ComponentPropsWithoutRef<typeof UIAlert.Icon> &
    VariantProps<typeof alertIconStyle> & {
        height?: number;
        width?: number;
    } & {
        as: React.ElementType;
    };

const AlertIcon = React.forwardRef<React.ElementRef<typeof UIAlert.Icon>, IAlertIconProps>(
    ({className, as: Component, ...props}, ref) => {
        const {action: parentAction, variant: parentVariant} = useStyleContext(SCOPE);

        return (
            <Component
                className={alertIconStyle({
                    parentVariants: {
                        action: parentAction,
                        variant: parentVariant
                    },
                    class: className
                })}
                {...props}
                ref={ref}
            />
        );
    }
);

export type AlertProps = IAlertProps & {
    title?: string;
    children: React.ReactNode;
    primaryText?: string;
    secondaryText?: string;
    onClose?: () => void;
    primaryAction?: () => void;
    secondaryAction?: () => void;
};

const Alert = React.forwardRef<React.ElementRef<typeof UIAlert>, AlertProps>(
    (
        {
            children,
            title,
            onClose,
            variant = "solid",
            action = "info",
            secondaryText,
            primaryText,
            secondaryAction,
            primaryAction,
            ...props
        },
        ref
    ) => {
        const Icon = useMemo(() => {
            const iconMap = {
                muted: InfoIcon,
                success: CheckedSolidCircleIcon,
                warning: AlertCircleIcon,
                error: AlertCircleIcon,
                info: InfoIcon
            } satisfies Record<Required<AlertProps>["action"], typeof InfoIcon>;

            return iconMap[action];
        }, [action]);
        const IconNode =
            variant === "banner" ? (
                <Center
                    className={iconWrapperOutsideStyle({
                        action
                    })}
                >
                    <Center className={iconWrapperInsideStyle({action})}>
                        <AlertIcon as={Icon} size="sm" />
                    </Center>
                </Center>
            ) : (
                <AlertIcon as={Icon} className="m-0.5" size="sm" />
            );

        const ActionNode =
            secondaryText || primaryText ? (
                <HStack className="h-auto md:gap-4 gap-2 md:items-center items-start">
                    {secondaryText && (
                        <Link onPress={secondaryAction}>
                            <LinkText
                                className={alertTextStyle({
                                    parentVariants: {
                                        action
                                    }
                                })}
                                variant="sm"
                            >
                                {secondaryText}
                            </LinkText>
                        </Link>
                    )}
                    {primaryText && (
                        <Link onPress={primaryAction}>
                            <LinkText
                                className={`font-bold ${alertTextStyle({
                                    parentVariants: {
                                        action
                                    }
                                })}`}
                                variant="sm"
                            >
                                {primaryText}
                            </LinkText>
                        </Link>
                    )}
                </HStack>
            ) : undefined;

        return (
            <AlertUI ref={ref} action={action} variant={variant} {...props}>
                <HStack className={alertContainerStyle({variant})}>
                    {IconNode}
                    <VStack className="flex-1 justify-center" space="sm">
                        <VStack space="xs">
                            {title && <AlertText variant="sm">{title}</AlertText>}
                            <AlertText isParagraph variant="sm">
                                {children}
                            </AlertText>
                        </VStack>
                        {ActionNode && <Box className="md:hidden">{ActionNode}</Box>}
                    </VStack>
                    {onClose && (
                        <Button size="sm" variant="ghost" onPress={onClose}>
                            <ButtonIcon as={CloseIcon} />
                        </Button>
                    )}
                    <Center className="hidden md:flex">{ActionNode}</Center>
                </HStack>
            </AlertUI>
        );
    }
);

Alert.displayName = "Alert";
AlertUI.displayName = "AlertUI";
AlertText.displayName = "AlertText";
AlertIcon.displayName = "AlertIcon";

export {Alert};
